// Product export — CSV and XLSX.
//
// The column list here is not decorative: it is the SAME list the importer
// reads, in the same order, so an exported file can be edited and fed straight
// back in. `PRODUCT_COLUMNS` is the single definition both sides use — if this
// export and CsvImportDialog ever disagree, the round-trip the owner asked for
// silently stops working.
//
// No PDF. A product list is worked on in a spreadsheet, not read on paper.

import type { Category, Product } from '@/types/domain';
import { buildCsvForExcel } from '@/lib/csv';
import { loadXlsx } from '@/lib/spreadsheet';

/** Column order, shared with the importer. Changing this changes both. */
export const PRODUCT_COLUMNS = [
  'sku',
  'name',
  'category',
  'brand',
  'price',
  'costPrice',
  'unit',
  'isActive',
  'description',
  'imageUrl',
] as const;

export interface ProductExportFilters {
  status?: 'all' | 'active' | 'inactive';
  search?: string;
}

/**
 * Category is exported as a NAME, never an id.
 *
 * A UUID in a spreadsheet cannot be edited by a person, which defeats the point
 * of exporting. mapProduct collapses category_id and legacy_category into one
 * `categoryId` field, so a value that matches no known category is legacy free
 * text and is already the name — pass it through.
 */
export function categoryName(p: Product, byId: Map<string, string>): string {
  if (!p.categoryId) return '';
  return byId.get(p.categoryId) ?? p.categoryId;
}

function toRow(p: Product, byId: Map<string, string>) {
  return {
    sku: p.sku,
    name: p.name,
    category: categoryName(p, byId),
    brand: p.brand ?? '',
    // Raw numbers, not formatted. "Rp 20.000" would come back as text and fail
    // to re-import; the sheet can format the column for display.
    price: p.price,
    costPrice: p.costPrice ?? '',
    unit: p.unit ?? 'pcs',
    isActive: p.isActive ? 'true' : 'false',
    description: p.description ?? '',
    imageUrl: p.imageUrl ?? '',
  };
}

export function filterProducts(
  products: Product[],
  filters: ProductExportFilters = {},
): Product[] {
  const { status = 'all', search = '' } = filters;
  const term = search.trim().toLowerCase();
  return products.filter((p) => {
    if (status === 'active' && !p.isActive) return false;
    if (status === 'inactive' && p.isActive) return false;
    if (term) {
      const hay = [p.sku, p.name, p.description ?? ''].join(' ').toLowerCase();
      if (!hay.includes(term)) return false;
    }
    return true;
  });
}

const nameById = (categories: Category[]) =>
  new Map(categories.map((c) => [c.id, c.name]));

export function buildProductCsv(
  products: Product[],
  categories: Category[],
  filters?: ProductExportFilters,
): string {
  const byId = nameById(categories);
  // buildCsvForExcel keys each row by header name, so toRow's shape maps
  // straight over. The `sep=` hint it prefixes is what makes the file open in
  // columns on an Indonesian Excel instead of stacked in column A.
  const rows = filterProducts(products, filters).map((p) => toRow(p, byId));
  return buildCsvForExcel([...PRODUCT_COLUMNS], rows);
}

export async function buildProductXlsx(
  products: Product[],
  categories: Category[],
  filters?: ProductExportFilters,
): Promise<Blob> {
  const XLSX = await loadXlsx();
  const byId = nameById(categories);
  const rows = filterProducts(products, filters).map((p) => toRow(p, byId));

  const aoa: (string | number)[][] = [
    [...PRODUCT_COLUMNS],
    ...rows.map((r) => PRODUCT_COLUMNS.map((c) => r[c] ?? '')),
  ];

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = aoa[0].map((_, i) => {
    const maxLen = aoa.reduce((m, row) => Math.max(m, String(row[i] ?? '').length), 0);
    return { wch: Math.min(Math.max(maxLen + 2, 10), 50) };
  });
  if (aoa.length > 1) ws['!freeze'] = { xSplit: 0, ySplit: 1 };

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Products');
  const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  return new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}
